import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { STAFF_API } from '../staffAuth';
import { useToast } from '../components/ui';

const Context = createContext(null);
const tones = {
  subtle: [{ frequency: 520, duration: 0.09, gain: 0.12 }],
  signal: [{ frequency: 620, duration: 0.1, gain: 0.16 }, { frequency: 820, duration: 0.12, gain: 0.14 }],
  urgent: [{ frequency: 760, duration: 0.09, gain: 0.2 }, { frequency: 980, duration: 0.09, gain: 0.18 }, { frequency: 760, duration: 0.12, gain: 0.2 }],
};

function claimAudioEvent(scope, id) {
  if (!id) return true;
  const key = `vroom:${scope}:audio:${id}`;
  const now = Date.now();
  try {
    const previous = Number(localStorage.getItem(key) || 0);
    if (now - previous < 30_000) return false;
    localStorage.setItem(key, String(now));
  } catch {}
  return true;
}

async function playTone(name = 'signal') {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) throw new Error('AUDIO_UNAVAILABLE');
  const context = new AudioContext();
  await context.resume();
  if (context.state !== 'running') {
    await context.close().catch(() => {});
    throw new Error('AUDIO_BLOCKED');
  }
  let start = context.currentTime;
  for (const note of tones[name] || tones.signal) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = note.frequency;
    gain.gain.setValueAtTime(note.gain, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + note.duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + note.duration);
    start += note.duration + 0.035;
  }
  window.setTimeout(() => context.close().catch(() => {}), 1000);
}

export function StaffNotificationProvider({ children }) {
  const toast = useToast();
  const [counts, setCounts] = useState({});
  const [settings, setSettings] = useState({ sound: 'signal', muted: false });
  const [audioBlocked, setAudioBlocked] = useState(false);
  const settingsRef = useRef(settings);
  const toastRef = useRef(toast);
  const seen = useRef(new Set());
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const reloadCounts = useCallback(async () => {
    const { data } = await axios.get(`${STAFF_API}/api/staff/notification-counts`);
    setCounts(data.counts || {});
    return data;
  }, []);
  const reloadSettings = useCallback(async () => {
    const { data } = await axios.get(`${STAFF_API}/api/staff/notification-settings`);
    setSettings(data);
    return data;
  }, []);
  const updateSettings = useCallback(async (patch) => {
    const { data } = await axios.patch(`${STAFF_API}/api/staff/notification-settings`, patch);
    setSettings(data);
    return data;
  }, []);
  const preview = useCallback(async (sound = settingsRef.current.sound) => {
    try { await playTone(sound); setAudioBlocked(false); } catch { setAudioBlocked(true); }
  }, []);

  useEffect(() => {
    reloadCounts().catch(() => {});
    reloadSettings().catch(() => {});
    const token = localStorage.getItem('adminToken');
    if (!token) return undefined;
    const socket = io(STAFF_API, { auth: { token }, transports: ['websocket', 'polling'] });
    const activity = async (event) => {
      if (!event?.id || seen.current.has(event.id)) return;
      seen.current.add(event.id);
      if (seen.current.size > 200) seen.current = new Set([...seen.current].slice(-100));
      reloadCounts().catch(() => {});
      toastRef.current(event.title || 'Nowe zdarzenie', 'info');
      if (!settingsRef.current.muted && claimAudioEvent('staff', event.id)) {
        try { await playTone(settingsRef.current.sound); setAudioBlocked(false); } catch { setAudioBlocked(true); }
      }
    };
    socket.on('staff:activity', activity);
    return () => socket.disconnect();
  }, [reloadCounts, reloadSettings]);

  const value = useMemo(() => ({
    audioBlocked,
    counts,
    preview,
    reloadCounts,
    settings,
    updateSettings,
  }), [audioBlocked, counts, preview, reloadCounts, settings, updateSettings]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useStaffNotifications() {
  const value = useContext(Context);
  if (!value) throw new Error('useStaffNotifications wymaga StaffNotificationProvider');
  return value;
}
